<template>
  <section>
    <h1 class="text-h3 mb-8">
      Text and typography
    </h1>
    <h2 class="text-h4 mb-4">
      Font family
    </h2>
    <p>Fonts are in order of priority.</p>
    <v-sheet
      v-for="font in fonts"
      :key="font"
      class="mb-4 px-6 py-4"
      outlined
      rounded
    >
      <div
        :style="{ fontFamily: font + ' !important' }"
      >
        <div class="font-size-title mb-2">
          {{ font }}
        </div>
        <div class="font-size-preview">
          中文字型預覽，<strong>粗體預覽。</strong><br>
          English font family preview, <strong>bold text preview.</strong>
        </div>
      </div>
    </v-sheet>
    <h2 class="text-h4 mb-4">
      Typography
    </h2>
    <p>
      Usage: <code>.text[-{breakpoint}]-{value}</code><br>
      e.g. <code>.text-h4</code>, <code>.text-h4.text-md-h3</code>
    </p>
    <v-sheet
      v-for="(typo, i) in typographies"
      :key="typo.suffix"
      class="mb-4 px-6 py-4"
      outlined
      rounded
    >
      <div
        ref="typoTargets"
        class="mb-2"
        :class="'text-' + typo.suffix"
      >
        .text-{{ typo.suffix }}
      </div>
      <div class="text-caption grey--text text--darken-1">
        {{ typo.size }} / {{ computedFontSizes[i] }}
      </div>
    </v-sheet>
    <h2 class="text-h4 mb-4">
      Font emphasis
    </h2>
    <v-sheet
      class="mb-4 px-6 py-4"
      outlined
      rounded
    >
      <p
        v-for="(emphasis, i) in fontEmphasis"
        :key="emphasis"
        :class="['font-' + emphasis, {
          'mb-0': i === fontEmphasis.length - 1
        }]"
      >
        .font-{{ emphasis }}
      </p>
    </v-sheet>
    <h2 class="text-h4 mb-4">
      Text Alignment
    </h2>
    <p>
      Usage: <code>.text[-{breakpoint}]-{value}</code><br>
      e.g. <code>.text-center</code>, <code>.text-center.text-md-left</code>
    </p>
    <v-sheet
      class="mb-4 px-6 py-4"
      outlined
      rounded
    >
      <p
        v-for="(alignment, i) in textAlignments"
        :key="alignment"
        :class="['text-' + alignment, {
          'mb-0': i === textAlignments.length - 1
        }]"
      >
        .text-{{ alignment }}
      </p>
    </v-sheet>
    <h2 class="text-h4 mb-4">
      Text Opacity
    </h2>
    <v-sheet
      class="mb-4 px-6 py-4"
      outlined
      rounded
    >
      <p
        v-for="(opacity, i) in textOpacities"
        :key="opacity"
        :class="['text--' + opacity, {
          'mb-0': i === textOpacities.length - 1
        }]"
      >
        .text--{{ opacity }}
      </p>
    </v-sheet>
    <h2 class="text-h4 mb-4">
      Wrapping and overflow
    </h2>
    <v-sheet
      class="mb-4 px-6 py-4"
      outlined
      rounded
    >
      <div class="grey--text text--darken-1 mb-1">
        <code>.text-no-wrap</code>
      </div>
      <div
        class="text-no-wrap secondary"
        style="width: 8rem;"
      >
        This text should overflow the parent.
      </div>
      <v-divider class="my-4" />
      <div class="grey--text text--darken-1 mb-1">
        <strong>Block level</strong><br>
        <code>.text-truncate</code>
      </div>
      <div class="row">
        <div class="col-2 text-truncate">
          Suspendisse faucibus, nunc et pellentesque egestas, lacus ante convallis tellus.
        </div>
      </div>
      <div class="grey--text text--darken-1 mt-2 mb-1">
        <strong>Inline level</strong><br>
        <code>.d-inline-block.text-truncate</code>
      </div>
      <span
        class="d-inline-block text-truncate"
        style="max-width: 150px;"
      >
        Suspendisse faucibus, nunc et pellentesque egestas, lacus ante convallis tellus.
      </span>
    </v-sheet>
    <h2 class="text-h4 mb-4">
      Further reading
    </h2>
    <external-link href="https://vuetifyjs.com/en/styles/text-and-typography/">
      <strong>Text and typography</strong><br>
      Text helper classes...
    </external-link>
    <div
      ref="fontTarget"
      hidden
    />
  </section>
</template>

<script>
export default {
  name: 'StylesTextAndTypography',
  data: () => ({
    fonts: [],
    typographies: [
      { suffix: 'h3', size: '3rem' },
      { suffix: 'h4', size: '2.125rem' },
      { suffix: 'h5', size: '1.5rem' },
      { suffix: 'h6', size: '1.25rem' },
      { suffix: 'subtitle-1', size: '1rem' },
      { suffix: 'subtitle-2', size: '0.875rem' },
      { suffix: 'body-1', size: '1rem' },
      { suffix: 'body-2', size: '0.875rem' },
      { suffix: 'button', size: '0.875rem' },
      { suffix: 'caption', size: '0.75rem' },
      { suffix: 'overline', size: '0.75rem' }
    ],
    computedFontSizes: [],
    fontEmphasis: [
      'weight-bold',
      'weight-regular'
    ],
    textAlignments: [
      'left',
      'center',
      'right'
    ],
    textOpacities: [
      'primary',
      'secondary',
      'disabled'
    ]
  }),
  mounted () {
    this.getFontFamily()
    this.getComputedFontSize()
  },
  methods: {
    getFontFamily () {
      const target = this.$refs.fontTarget
      if (target) {
        const style = getComputedStyle(target)
        const fontFamily = style.fontFamily
        this.fonts = fontFamily
          .split(',')
          .map(font => font.trim().replace(/"/g, ''))
      }
    },
    getComputedFontSize () {
      const targets = this.$refs.typoTargets
      if (targets) {
        this.computedFontSizes = targets.map(target => {
          const style = getComputedStyle(target)
          return style.fontSize
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles';

.font-size-title {
  font-size: map-get($headings, 'h5', 'size');
}
.font-size-preview {
  font-size: map-get($headings, 'h4', 'size');
}
</style>
